import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilState, useRecoilValue } from "recoil";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import { accountAtom } from "../../../Login/atoms/AccountAtom";
import { useSFGetOffice } from "../../OfficeSettings/hooks/useSFGetOffice";
import {
  prodCodeCarrierName,
  prodCodeAddState,
  prodCodeEditState,
  producerCodeMastercarrierId,
  officeData,
} from "../atom";
import TextInputHookFormDisabled from "../../../../components/TextInputHookFormDisabled";
import { useGetCarrierNameList } from "../../Carrier/hooks/useGetCarrierNameList";
import SelectHookForm4 from "../../../../components/SelectHookForm_4";
import { useGetProgressiveCodes } from "../hooks/useGetProgressiveCodes";

interface ProducerCodeCarrierSectionProp {
  empFranchisesOpts?: any;
  confieEmpOpts?: any;
  employeeListLoading;
  franchiseType;
}

export const ProducerCodeCarrierSection: React.FC<
  ProducerCodeCarrierSectionProp
> = ({ empFranchisesOpts, confieEmpOpts, employeeListLoading }) => {
  const user = useRecoilValue(accountAtom);
  const [isAddState] = useRecoilState(prodCodeAddState);
  const [isEditState] = useRecoilState(prodCodeEditState);
  const [masterCarrierId] = useRecoilState(producerCodeMastercarrierId);
  const [officeEditData] = useRecoilState(officeData);
  const [carrierName] = useRecoilState(prodCodeCarrierName);

  const {
    control,
    formState: { errors },
    register,
    watch,
    setValue,
  } = useFormContext();

  const { data: carriersQuery, isLoading: carrierLoading } =
    useGetCarrierNameList();
  carriersQuery?.find((c) => c.masterCarrierId === watch("carrierId"));
  const carriers: any = Array.from(
    new Map(
      carriersQuery?.map((x) => [
        x.masterCarrierId,
        {
          label: x.carrierName,
          value: x.masterCarrierId,
        },
      ]),
    ).values(),
  );

  const { data: progressiveCodes, isLoading: progressiveCodesLoading } =
    useGetProgressiveCodes();
  const progressiveCodesOpts = progressiveCodes?.map((x) => {
    return {
      label: x.producerCode,
      value: x.producerCode,
    };
  });

  const associationValues = [
    { label: "Office", value: "Office" },
    {
      label: "Producer",
      value: "Producer",
    },
  ];
  const franchiseAssociatedValues = [{ label: "Office", value: "Office" }];

  const superUser = user?.isNonFranchise && user?.isFranchise;
  const confie = user?.isNonFranchise && !user?.isFranchise;
  const franchise = !user?.isNonFranchise && user?.isFranchise;

  const isFranchiseWatch = watch("isFranchise");

  useEffect(() => {
    if (isFranchiseWatch === true) {
      setValue("associatedTo", "Office");
    }
    if (franchise) {
      setValue("isFranchise", true);
      setValue("associatedTo", "Office");
    }
    if (confie) {
      setValue("isFranchise", false);
    }
  }, [isFranchiseWatch, franchise, setValue, confie]);

  const { data: agenciesQuery, isFetching: isLoading } = useSFGetOffice();

  const franchiseOffice =
    agenciesQuery &&
    agenciesQuery
      ?.filter(
        (x) =>
          x.officeCode !== null &&
          x.officeCode !== "" &&
          x.officeType === "Franchise",
      )
      .map((resData) => ({
        label: resData.officeName + "-" + resData.officeCode,
        value: resData.officeCode,
        storeNumber: resData.storeNumber,
        state: resData.stateCode,
        isFranchise: resData.isFranchise,
        officeType: resData.officeType,
        channel: resData.channelC,
        officeOrigin: resData.officeOrigin,
        officeTypeId: resData.officeTypeId,
        officeStatusDetailsName: resData.officeStatusDetails,
        relatedOfficeCode: resData.officeRelated,
      }));

  const confieOffice =
    agenciesQuery &&
    agenciesQuery
      ?.filter(
        (x) =>
          x.officeCode !== null &&
          x.officeCode !== "" &&
          x.officeType !== "Franchise",
      )
      .map((resData) => ({
        label: resData.officeName + "-" + resData.officeCode,
        value: resData.officeCode,
        storeNumber: resData.storeNumber,
        state: resData.stateCode,
        isFranchise: resData.isFranchise,
        officeType: resData.officeType,
        channel: resData.channelC,
        officeOrigin: resData.officeOrigin,
        officeTypeId: resData.officeTypeId,
        officeStatusDetailsName: resData.officeStatusDetails,
        relatedOfficeCode: resData.officeRelated,
      }));

  useEffect(() => {
    if (masterCarrierId) {
      setValue("carrierId", masterCarrierId);
      setValue("masterCarrierId", masterCarrierId);
      setValue("carrierName", carrierName);
    }
    if (isEditState) {
      if (watch("isFranchise") === true) {
        setValue("distributionChannel", "Franchise");
      } else {
        setValue("distributionChannel", officeEditData?.distributionChannel);
      }
      setValue(
        "officeStatusDetailsName",
        officeEditData?.officeStatusDetailsName,
      );
      setValue("relatedOfficeCode", officeEditData?.relatedOfficeCode);
    }
  }, [
    masterCarrierId,
    isEditState,
    carrierName,
    officeEditData,
    setValue,
    watch,
  ]);

  // Handle isInProduction
  const carrierId = watch("carrierId");
  useEffect(() => {
    if (!isAddState) return;

    if (carrierId && carriersQuery?.length) {
      const selectedCarrier = carriersQuery.find(
        (c) => c.masterCarrierId === carrierId,
      );

      setValue("isInProduction", selectedCarrier?.isDigitized === true);
    } else {
      setValue("isInProduction", false);
    }
  }, [carrierId, isAddState, carriersQuery, setValue]);

  const setExceptionBoxes = (val: boolean) => {
    setValue("isCDRCException", val);
    setValue("isKeeperException", val);
    setValue("isAL3Exception", val);
  };

  return (
    <>
      <div className="grid lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-3 items-center w-full gap-y-2 gap-x-5 mb-2">
        <Controller
          control={control}
          name="carrierId"
          rules={{ required: true }}
          render={({ field }) => (
            <div className="flex flex-col w-full">
              <SelectHookForm4
                label="Carrier Name"
                value={field.value}
                options={carriers?.filter((x) => x.label !== "")}
                isLoading={carrierLoading}
                field={field}
                onChange={(selectedOptions) => {
                  const carrierId = selectedOptions?.value ?? null;
                  const carrierName = selectedOptions?.label ?? "";

                  field.onChange(carrierId);
                  setValue("carrierName", carrierName);
                  setValue("masterCarrierId", carrierId);
                  setValue("carrierId", carrierId);

                  if (carrierId) {
                    setValue("carrierProgram", null);
                    setValue("rollUpId", null);
                    setValue("masterCode", null);
                  }
                  // Handle isInProduction
                  if (carrierId && carriersQuery?.length) {
                    const selectedCarrier = carriersQuery.find(
                      (c) => c.masterCarrierId === carrierId,
                    );
                    const isDigitized = selectedCarrier?.isDigitized === true;
                    setValue("isInProduction", isDigitized);
                  } else {
                    setValue("isInProduction", false);
                  }
                  // Handle Carrier National Safe Drivers
                  if (carrierId === 174 && watch("status") !== "false") {
                    setExceptionBoxes(true);
                  } else {
                    setExceptionBoxes(false);
                  }
                }}
                className={
                  errors.carrierId && "border-2 border-red-500 col-span-2 "
                }
                required={true}
                isDisabled={masterCarrierId}
              />
            </div>
          )}
        />
        {[20].includes(watch("carrierId")) && (
          <TextInputLabelHookForm
            // Princeton Agent Code = Agent ID
            name="princetonAgentCode"
            label="Agent ID"
            type="text"
            register={register("princetonAgentCode", {
              required: {
                value: true,
                message: "Required Field",
              },
            })}
            required={true}
            className={
              errors.princetonAgentCode && "border-2 border-red-500 col-span-2 "
            }
            secondDiv="grid grid-cols-3"
            placeholder={undefined}
          />
        )}
        {[20].includes(watch("carrierId")) && (
          <TextInputLabelHookForm
            name="masterAgentPrincetonId"
            label="Master Agent ID"
            type="text"
            register={register("masterAgentPrincetonId", {})}
            onChange={(e) => {
              const princetonId = e.target.value
                .replaceAll(/\D/g, "")
                .match(/(\d{0,9})/);
              if (princetonId) {
                e.target.value = princetonId[1];
              }
            }}
            maxLength={15}
            secondDiv="grid grid-cols-3"
            placeholder={undefined}
          />
        )}
        {[60].includes(watch("carrierId")) && (
          <Controller
            control={control}
            name="progressiveCode"
            render={({ field }) => (
              <SelectHookFormSingle
                label="Progressive Code"
                value={field.value ?? null}
                options={progressiveCodesOpts}
                isLoading={progressiveCodesLoading}
                field={field}
                onChange={(selectedOptions) => {
                  field.onChange(selectedOptions?.value ?? null);
                }}
                secondDiv="grid grid-cols-3 md:grid-cols-3 lg:md:grid-cols-3 sm: md:grid-cols-3 w-full items-center gap-2"
              />
            )}
          />
        )}
      </div>
      <div className="border-b border-gray-200 mb-2"></div>
      <div className="grid lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-3 items-center w-full gap-y-2 gap-x-5">
        <Controller
          control={control}
          name="associatedTo"
          rules={{ required: true }}
          render={({ field }) => (
            <SelectHookFormSingle
              label="Associated To"
              value={field.value ?? null}
              options={
                superUser || confie
                  ? associationValues
                  : franchiseAssociatedValues
              }
              field={field}
              onChange={(selectedOptions) => {
                field.onChange(selectedOptions?.value ?? null);
                setValue("associatedTo", selectedOptions?.label ?? "");
                setValue("distributionChannel", null);
              }}
              required={true}
              className={
                errors.associatedTo && "border-2 border-red-500 col-span-2"
              }
              secondDiv={`${
                watch("associatedTo") === "" ? "mt-5" : ""
              } grid grid-cols-3 md:grid-cols-3 lg:md:grid-cols-3 sm: md:grid-cols-3 w-full items-center gap-2`}
              isDisabled={watch("isFranchise")}
            />
          )}
        />
        {watch("associatedTo") === "Office" ||
        watch("associatedTo") === "Producer" ? (
          <div>
            {["Office"].includes(watch("associatedTo")) && (
              <Controller
                control={control}
                name="officeCode"
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectHookFormSingle
                    label="Office"
                    value={field.value ?? null}
                    options={
                      watch("isFranchise") || franchise
                        ? franchiseOffice
                        : confieOffice
                    }
                    isLoading={isLoading}
                    field={field}
                    onChange={(selectedOptions) => {
                      field.onChange(selectedOptions?.value ?? null);

                      setValue("officeCode", selectedOptions?.value ?? "");
                      setValue(
                        "storeNumber",
                        selectedOptions?.storeNumber ?? null,
                      );
                      setValue(
                        "officeStatusDetailsName",
                        selectedOptions?.officeStatusDetailsName ?? "",
                      );
                      setValue(
                        "relatedOfficeCode",
                        selectedOptions?.relatedOfficeCode ?? "",
                      );

                      setValue(
                        "franchiseTypeId",
                        selectedOptions?.value.toLowerCase().startsWith("z") &&
                          isFranchiseWatch
                          ? 1
                          : null,
                      );
                      if (
                        selectedOptions?.officeType?.toLowerCase() ===
                        "franchise"
                      ) {
                        setValue(
                          "distributionChannel",
                          selectedOptions?.officeType ?? null,
                        );
                      } else {
                        setValue(
                          "distributionChannel",
                          selectedOptions?.channel ?? null,
                        );
                      }
                    }}
                    required={true}
                    className={
                      errors.officeCode && "border-2 border-red-500 col-span-2 "
                    }
                    secondDiv="grid grid-cols-3 md:grid-cols-3 lg:md:grid-cols-3 sm: md:grid-cols-3  w-full items-center gap-2"
                  />
                )}
              />
            )}
            {["Producer"].includes(watch("associatedTo")) &&
              watch("distributionChannelId") !== 6 &&
              (confie || superUser) && (
                <Controller
                  control={control}
                  name="producer"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <SelectHookFormSingle
                      label="Producer"
                      value={field.value ?? null}
                      options={
                        watch("distributionChannelId") === 6
                          ? empFranchisesOpts
                          : confieEmpOpts
                      }
                      field={field}
                      onChange={(selectedOptions) => {
                        field.onChange(selectedOptions?.value ?? null);
                        setValue(
                          "distributionChannel",
                          selectedOptions?.channel ?? null,
                        );
                      }}
                      required={true}
                      className={
                        errors.producer && "border-2 border-red-500 col-span-2 "
                      }
                      secondDiv="grid grid-cols-3 md:grid-cols-3 lg:md:grid-cols-3 sm: md:grid-cols-3  w-full items-center gap-2"
                      isLoading={employeeListLoading}
                    />
                  )}
                />
              )}
          </div>
        ) : (
          <>
            {" "}
            <div className=" grid grid-cols-3 ">
              <div className="text-xs text-secondary italic font-normal mb-1 ">
                <b> Note:</b> Select in Associated To field first.
              </div>
              <div className="col-span-2">
                <SelectHookFormSingle
                  isDisabled={true}
                  options={undefined}
                  secondDiv="grid items-center col-span-2"
                />
              </div>
            </div>
          </>
        )}

        {["Office"].includes(watch("associatedTo")) && (
          <>
            <TextInputHookFormDisabled
              name="officeCode"
              type="text"
              label="Office Code"
              register={register("officeCode", {})}
            />
            <TextInputHookFormDisabled
              name="officeStatusDetailsName"
              type="text"
              label="Office Status Details"
              register={register("officeStatusDetailsName", {})}
            />
            <TextInputHookFormDisabled
              name="relatedOfficeCode"
              type="text"
              label="Related Office Code"
              register={register("relatedOfficeCode", {})}
            />
          </>
        )}
        <TextInputHookFormDisabled
          name="distributionChannel"
          type="text"
          label="Distribution Channel"
          register={register("distributionChannel", {})}
        />
      </div>
    </>
  );
};
